/**
 * @flow
 */
import Box from '@mui/material/Box';
import { Button, SvgIcon, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import FillFormDrawerQuestions from 'components/@home/drawers/FillFormDrawer/FillFormDrawerQuestions';
import QuestionErrorMessage from 'components/@home/drawers/FormDrawer/QuestionErrorMessage';
import { FieldArray, Form, useFormikContext } from 'formik';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import LockIcon from 'mdi-react/LockIcon';
import LockOpenIcon from 'mdi-react/LockOpenIcon';
import React from 'react';
import { I18n } from 'utils/i18n';

const StyledForm = styled(Form)({
  flexGrow: 1,
  padding: '20px',
  maxWidth: 'calc(100% - 60px)',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'relative',
});

const FillFormDrawerContent = () => {
  const formik = useFormikContext();
  const {
    isSubmitting,
    isValid,
    submitCount,
    errors,
    values: { form, currentQuestion, disabled },
  } = formik;

  if (form?.error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography>{I18n.t('FillFormDrawer.Error loading form')}</Typography>
      </Box>
    );
  }
  const showSubmit = currentQuestion === form.questions?.length;
  const lastQuestion = currentQuestion === form.questions?.length - 1;

  const showError = !isValid && submitCount > 0;

  return (
    <StyledForm>
      <Typography variant="h6" component="h1">
        {form.title}
      </Typography>
      <>
        {form.anonymous && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pt: '20px',
            }}
          >
            <Box
              component="span"
              sx={theme => ({
                fontSize: '12px',
                fontWeight: '500',
                textTransform: 'uppercase',
                mb: '5px',
                color: theme.palette.primary.main,
              })}
            >
              {I18n.t('FillFormDrawer.AnonymousAnswers')}
            </Box>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 300ms, transform 300ms',
            opacity: showSubmit && !disabled ? 1 : 0,
            height: 0,
            transform: showSubmit && !disabled ? 'translateY(0%)' : 'translateY(500px)',
            overflow: 'visible',
            pointerEvents: showSubmit && !disabled ? 'initial' : 'none',
          }}
        >
          <Typography
            variant="h6"
            component="h6"
            sx={theme => ({
              mt: theme.spacing(40),
              mb: theme.spacing(2),
            })}
          >
            {I18n.t('FillFormDrawer.I hereby confirm', { title: form.title })}
          </Typography>
          <Typography
            sx={theme => ({
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              '& svg': {
                marginRight: theme.spacing(0.5),
                color: theme.palette.secondary.main,
              },
            })}
          >
            <SvgIcon>{form.adminOnly ? <LockIcon /> : <LockOpenIcon />}</SvgIcon>
            {I18n.t(
              `FillFormDrawer.Answer${form.adminOnly ? 'Private' : 'Public'}${
                form.anonymous ? 'Anonymous' : ''
              }`,
            )}
          </Typography>
          <Button
            variant="contained"
            disabled={isSubmitting || showError}
            sx={theme => ({
              pl: theme.spacing(5),
              pr: theme.spacing(5),
              backgroundColor: theme.palette.tertiary.dark,
              mt: theme.spacing(4),
              '&:hover': {
                backgroundColor: theme.palette.tertiary.main,
              },
            })}
            color="primary"
            type="submit"
          >
            {isSubmitting
              ? I18n.t('FillFormDrawer.Sending answers')
              : I18n.t('FillFormDrawer.Send answers')}
          </Button>
          <Typography variant="caption">
            {I18n.t('FillFormDrawer.When you click submit')}
          </Typography>
        </Box>
        <Box
          sx={{
            overflow: 'auto',
            pb: '20px',
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
          }}
        >
          <Box
            component="fieldset"
            sx={{
              border: 0,
              p: 0,
              m: 0,
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
            disabled={disabled}
          >
            <FieldArray name="questions" component={FillFormDrawerQuestions} />
          </Box>{' '}
        </Box>
        {!showSubmit && (
          <Box
            sx={theme => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            })}
          >
            <Box>
              {showError ? (
                <QuestionErrorMessage>
                  {errors.server || I18n.t('FillFormDrawer.Please check errors above')}
                </QuestionErrorMessage>
              ) : null}
            </Box>
            <Box>
              {!disabled && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'inline',
                    mr: '1em',
                  }}
                >
                  {I18n.t('FillFormDrawer.When you click continue')}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'inline',
                }}
              >
                {currentQuestion + 1}/{form.questions?.length}
              </Typography>
              <Button
                sx={theme => ({ ml: theme.spacing(2) })}
                variant="contained"
                disabled={isSubmitting || showError}
                color="primary"
                type="sumbit"
              >
                {lastQuestion && disabled
                  ? I18n.t('FillFormDrawer.Close')
                  : I18n.t('FillFormDrawer.Continue')}
                {!(lastQuestion && disabled) && (
                  <SvgIcon
                    sx={{
                      mt: '1px',
                      ml: '5px',
                      fontSize: 17,
                    }}
                  >
                    <ArrowRightIcon />
                  </SvgIcon>
                )}
              </Button>
            </Box>
          </Box>
        )}
      </>
    </StyledForm>
  );
};

export default FillFormDrawerContent;
